import { SortableProperty } from '@sdir/utilities/lib/types/sorting';
import { Moment } from 'moment';
import { ReactElement } from 'react';

export interface TableColumn<T = any> extends SortableProperty<T> {
  label: string;
}

export interface BaseTableRow<TChildTable = any> {
  childTable?: TableDefinition<TChildTable>;
  childContent?: ReactElement;
}

export interface TableDefinition<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  collapsable?: boolean;
  collapsableText?: [string, string];
  sortableKeys?: string[] | '*';
}

export const ApplicationsModalHeaders = (fm) => {
  return [
    {
      key: 'status',
      label: fm({ id: 'certificates.table.label.status' }),
    },
    {
      key: 'certificate',
      label: fm({ id: 'certificates.table.label.certificate' }),
    },
    {
      key: 'created',
      label: fm({ id: 'certificates.table.label.created' }),
    },
    { key: 'actions', label: '' },
  ] as TableColumn[];
};

export const AllCertificatesHeaders = (fm) => {
  return [
    {
      key: 'certificate',
      label: fm({ id: 'certificates.table.label.certificate' }),
    },
    {
      key: 'expiry',
      label: fm({ id: 'certificates.table.label.expiry' }),
    },
    { key: 'actions', label: '' },
  ] as TableColumn[];
};

export const AllEndorsementsHeaders = (fm) => {
  return [
    {
      key: 'name',
      label: fm({ id: 'endorsements.page.name' }),
    },
    {
      key: 'issuedAt',
      label: fm({ id: 'endorsements.page.issued' }),
    },
    { key: 'actions', label: '' },
  ] as TableColumn[];
};

export const AllDispensationsHeaders = (fm) => {
  return [
    {
      key: 'name',
      label: fm({ id: 'certificates.table.label.dispensation' }),
    },
    {
      key: 'validTo',
      label: fm({ id: 'certificates.table.label.validto' }),
    },
    { key: 'actions', label: '' },
  ] as TableColumn[];
};

export const ActiveApplicationsHeaders = (fm) => {
  return [
    {
      key: 'certificate',
      label: fm({ id: 'certificates.table.label.activeapplications' }),
    },
    { key: 'status', label: '' },
    { key: 'actions', label: '' },
  ] as TableColumn[];
};

export const SidCertificateHeaders = (fm) => {
  return [
    {
      key: 'documentNumber',
      label: fm({ id: 'certificates.table.label.documentnumber' }),
    },
    { key: 'status', label: '' },
    { key: 'issuedDate', label: fm({ id: 'certificates.table.label.issued' }) },
    { key: 'expiryDate', label: fm({ id: 'certificates.table.label.expiry' }) },
    { key: 'actions', label: '' },
  ] as TableColumn[];
};

export const SidApplicationsHeaders = (fm) => {
  return [
    {
      key: 'certificate',
      label: fm({ id: 'certificates.table.label.application' }),
    },
    { key: 'status', label: '' },
    { key: 'created', label: fm({ id: 'certificates.table.label.created' }) },
    { key: 'actions', label: '' },
  ] as TableColumn[];
};

export const SeagoingDetailsHeaderMobile = (fm) => {
  return [
    {
      label: fm({ id: 'addseagoing.table.jobTitle' }),
      key: 'jobTitle',
    },
  ] as TableColumn[];
};

export const SeagoingDetailsHeader = (fm) => {
  return [
    {
      label: fm({ id: 'addseagoing.table.jobTitle' }),
      key: 'jobTitle',
    },
    {
      label: fm({ id: 'addseagoing.table.periodStart' }),
      key: 'periodStart',
    },
    {
      label: fm({ id: 'addseagoing.table.periodEnd' }),
      key: 'periodEnd',
    },
    {
      label: fm({ id: 'addseagoing.table.hours' }),
      key: 'hours',
    },
    {
      label: '',
      key: 'actions',
    },
  ] as TableColumn[];
};

export interface SeagoingRow extends BaseTableRow<SeagoingChildTableRow> {
  occupation: string;
  days: string;
  daysValue: number;
}

export const SeagoingTableHeader = (fm) => {
  return [
    {
      label: fm({ id: 'seagoing.table.occupation' }),
      key: 'occupation',
    },
    {
      label: fm({ id: 'seagoing.table.totaltimedays' }),
      key: 'days',
      sortKey: 'daysValue',
    },
  ] as TableColumn<SeagoingRow>[];
};

export interface SeagoingChildTableRow {
  vessel: any;
  period: string;
  periodStart: Moment;
  periodEnd: Moment;
  employer: string;
  days: string;
  daysValue: number;
  details: ReactElement;
}

export const SeagoingChildTableHeaderMobile = (fm) => {
  return [
    {
      label: fm({ id: 'seagoing.table.childtable.vessel' }),
      key: 'vessel',
    },
    {
      label: '',
      key: 'details',
    },
  ] as TableColumn<SeagoingChildTableRow>[];
};

export const SeagoingChildTableHeader = (fm) => {
  return [
    {
      label: fm({ id: 'seagoing.table.childtable.vessel' }),
      key: 'vessel',
    },
    {
      label: fm({ id: 'seagoing.table.childtable.period' }),
      key: 'period',
      sortFunction: (a, b) => {
        const startDiff = a.periodStart.diff(b.periodStart);
        if (startDiff !== 0) return startDiff;
        return a.periodEnd.diff(b.periodEnd);
      },
    },
    {
      label: fm({ id: 'seagoing.table.childtable.employer' }),
      key: 'employer',
    },
    {
      label: fm({ id: 'seagoing.table.totaltimedays' }),
      key: 'days',
      sortKey: 'daysValue',
    },
    {
      label: '',
      key: 'details',
    },
  ] as TableColumn<SeagoingChildTableRow>[];
};

export interface NewSeagoingTableRow {
  status: any;
  period: string;
  vessel: string;
  occupation: string;
  seagoingTime: string;
  actions: any;
}
export const NewSeagoingTableColumns = (fm) => {
  return [
    {
      label: '',
      key: 'status',
    },
    {
      label: fm({ id: 'seagoing.table.period' }),
      key: 'period',
    },
    {
      label: fm({ id: 'seagoing.table.vessel' }),
      key: 'vessel',
    },
    {
      label: fm({ id: 'seagoing.table.occupation' }),
      key: 'occupation',
    },
    {
      label: fm({ id: 'seagoing.table.seagoingtime' }),
      key: 'seagoingTime',
    },
    {
      label: '',
      key: 'actions',
    },
  ] as TableColumn<NewSeagoingTableRow>[];
};

export const NewSeagoingTableColumnsMobile = (fm) => {
  return [
    {
      label: '',
      key: 'status',
    },
    {
      label: fm({ id: 'seagoing.table.vessel' }),
      key: 'vessel',
    },
    {
      label: fm({ id: 'seagoing.table.seagoingtime' }),
      key: 'seagoingTime',
    },
  ] as TableColumn<NewSeagoingTableRow>[];
};

export interface RequirementRow {
  type: string;
  requirement: string;
  uploadAction: any;
}
export const RequirementsTableHeader = (fm) => {
  return [
    {
      label: fm({ id: 'app.requirements.table.type' }),
      key: 'type',
    },
    {
      label: fm({ id: 'app.requirements.table.requirement' }),
      key: 'requirement',
    },
    {
      label: fm({ id: 'app.requirements.table.upload' }),
      key: 'uploadAction',
    },
  ] as TableColumn<RequirementRow>[];
};

export interface SharedCompetenceRow {
  organization: string;
  date: string;
  actions: any;
}
export const SharedCompetenceTableHeader = (fm) => {
  return [
    {
      label: fm({ id: 'profile.shared.table.organization' }),
      key: 'organization',
    },
    {
      label: fm({ id: 'profile.shared.table.created' }),
      key: 'date',
    },
    {
      label: '',
      key: 'actions',
    },
  ] as TableColumn<SharedCompetenceRow>[];
};
export interface ShareRequestCompetenceRow {
  organization: string;
  date: string;
  status: string;
  actions: any;
}
export const ShareRequestCompetenceTableHeader = (fm) => {
  return [
    {
      label: fm({ id: 'profile.shared.table.organization' }),
      key: 'organization',
    },
    {
      label: fm({ id: 'profile.shared.table.expires' }),
      key: 'date',
    },
    {
      label: fm({ id: 'profile.shared.table.status' }),
      key: 'status',
    },
    {
      label: '',
      key: 'actions',
    },
  ] as TableColumn<ShareRequestCompetenceRow>[];
};

export interface HealthRow {
  healthcheck: string;
  validTo: string;
  colorVisionDate: string;
}
export const HealthTableHeader = (fm): TableColumn<HealthRow>[] => {
  return [
    {
      label: fm({ id: 'competence.table.health.healthcheck' }),
      key: 'healthcheck',
    },
    {
      label: fm({ id: 'competence.table.health.validTo' }),
      key: 'validTo',
    },
    {
      label: fm({ id: 'competence.table.health.colorVisionDate' }),
      key: 'colorVisionDate',
    },
  ];
};
